import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import { DeepPartial } from '../../../../Interfaces/DeepPartial';
import type {
	CSSZSickLeaveSickLeaveSubmitPayload,
	CSSZSickLeaveValues,
} from '../CSSZSickLeaveCertificateCreate/CSSZSickLeaveCertificateCreate';
import { useRef, useState } from 'react';
import { isCSSZAddressComplete } from '../../Helpers/CSSZ.helper';
import useNotification from '../../../../Hooks/useNotification.hook';
import { CSSZSickLeaveCreateData } from '../../Interfaces/CSSZ.interface';
import useLoading from '../../../Base/Hooks/useLoading';
import { prepareDataForApi } from '../../Helpers/prepareSickLeave.helper';

export default function useCSSZSickLeaveSender(
	patientId: string,
	onCreate?: (sickLeaveDocumentId?: string) => void | Promise<void>,
) {
	const { displayError } = useApiClient();
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const isSendingRef = useRef(false);
	const [collectedValues, setCollectedValues] = useState<
		DeepPartial<CSSZSickLeaveValues>
	>({});
	const { isLoading, setIsLoading } = useLoading(false);

	const sendData = async (payload: CSSZSickLeaveCreateData) => {
		setIsLoading(true);
		const response = await apiClient.post(
			`/user/${patientId}/sick-leave/`,
			payload,
		);

		if (response.status === 200 || response.status === 502) {
			await onCreate?.(response.data?.documentId);
			showNotificationSuccess({
				content: 'component.CSSZSickLeaveCertificateCreate.successMessage',
			});
		}

		setIsLoading(false);
		return response;
	};

	const validateData = (values: DeepPartial<CSSZSickLeaveValues>): boolean => {
		let pass = true;

		if (
			!values.patientAddress ||
			!isCSSZAddressComplete(values.patientAddress)
		) {
			showNotificationAlert({
				content: 'validation.CSSZSickLeaveSender.missingPatientAddress',
			});
			pass = false;
		}

		if (!values.employment) {
			showNotificationAlert({
				content: 'validation.CSSZSickLeaveSender.missingEmployer',
			});
			pass = false;
		}

		if (!values.sickLeave) {
			showNotificationAlert({
				content: 'validation.CSSZSickLeaveSender.missingSickLeaveData',
			});
			pass = false;
		}

		return pass;
	};

	const requestSend = async (
		valuesOverride?: DeepPartial<CSSZSickLeaveValues>,
	) => {
		if (isSendingRef.current) return;

		const valuesToSend = valuesOverride ?? collectedValues;

		if (!validateData(valuesToSend)) return;

		isSendingRef.current = true;

		try {
			const sickLeave = valuesToSend.sickLeave as
				| CSSZSickLeaveSickLeaveSubmitPayload
				| undefined;
			const submitToCssz = sickLeave?.submitToCssz ?? true;

			const transformedData = prepareDataForApi(
				valuesToSend as CSSZSickLeaveValues,
			);
			await sendData({
				...transformedData,
				submitToCssz,
			});
		} catch (e) {
			displayError(e);
		} finally {
			isSendingRef.current = false;
		}
	};

	return {
		collectedValues,
		setCollectedValues,
		requestSend,
		isLoading,
	};
}
